import SolarSystem from '../main/objects/system';
import CelestialBody, { OrbitingBody } from '../main/objects/body';
import MultiFlyby from '../main/objects/multiflyby';

import { isInvalidOrbitInput, searchInputsFromUI } from '../utils';

import MissionControls from '../components/Flyby/MissionControls'
import EvolutionPlot from '../components/Flyby/EvolutionPlot';
import OrbitDisplayTabs from '../components/Flyby/OrbitDisplayTabs';
import MultiFlybyInfo from '../components/Flyby/MultiFlybyInfo';
import Navbar from '../components/Navbar';
import HelpCollapse from '../components/Flyby/HelpCollapse';
import DashboardPanel from '../components/DashboardPanel';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ScanningOverlay from '../components/ScanningOverlay';

import React, { useState, useEffect } from "react";
import CssBaseline from '@mui/material/CssBaseline';

import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import SearchIcon from '@mui/icons-material/Search';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

import { useAtom } from 'jotai';
import { multiFlybyAtom, multiFlybyEndOrbitAtom, multiFlybyStartOrbitAtom, flybyIdSequenceAtom, systemAtom, timeSettingsAtom, multiFlybyEarlyStartDateAtom, multiFlybyLateStartDateAtom, multiFlybyFlightTimesAtom, multiFlybyControlsOptionsAtom, multiFlybyDSNperLegAtom} from '../App';

export function blankMultiFlyby(system: SolarSystem): MultiFlyby {
  const orbit = (system.bodyFromId(1) as OrbitingBody).orbit;
  return new MultiFlyby({
    system:                 system,
    startOrbit:             orbit,
    endOrbit:               orbit,
    flybyIdSequence:        [],
    DSMparams:              [],
    transferBody:           system.sun,
    startDate:              0,
    flightTimes:            [],
    endDate:                426 * 6 * 3600,
    transfers:              [],
    ejections:              [],
    insertions:             [],
    flybys:                 [],
    maneuvers:              [],
    maneuverContexts:       [],
    deltaV:                 0,
    soiPatchPositions:      [],
    flybyDurations:         [],
    ejectionInsertionType:  'fastdirect',
    planeChange:            false,
    matchStartMo:           false,
    matchEndMo:             false,
    noInsertionBurn:        false,
    patchPositionError:     0.0,
    patchTimeError:         0.0,
  });
}

///// App Content /////
function FlybyAppContent() {
  
  const [system] = useAtom(systemAtom);
  const [timeSettings] = useAtom(timeSettingsAtom);
  const [multiFlyby, setMultiFlyby] = useAtom(multiFlybyAtom);
  const [startOrbit] = useAtom(multiFlybyStartOrbitAtom);
  const [endOrbit] = useAtom(multiFlybyEndOrbitAtom);
  const [flybyIdSequence] = useAtom(flybyIdSequenceAtom);
  const [earlyStartDate] = useAtom(multiFlybyEarlyStartDateAtom);
  const [lateStartDate] = useAtom(multiFlybyLateStartDateAtom);
  const [flightTimes] = useAtom(multiFlybyFlightTimesAtom);
  const [DSNperLeg] = useAtom(multiFlybyDSNperLegAtom);
  const [controlsOptionsState] = useAtom(multiFlybyControlsOptionsAtom);

  const [mfSearchInputs, setMfSearchInputs] = useState<MultiFlybySearchInputs | null>(null);
  const [invalidInput, setInvalidInput] = useState(false);
  const [buttonPresses, setButtonPresses] = useState(0);
  const [calculating, setCalculating] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // UI State
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [plotOpen, setPlotOpen] = useState(false);

  function handleButtonPress() {
    // update orbits
    let invalid = isInvalidOrbitInput(startOrbit.orbit) || isInvalidOrbitInput(endOrbit.orbit);

    let transferBody: CelestialBody;
    try{
      transferBody = system.bodyFromId(system.commonAttractorId(startOrbit.orbit.orbiting, endOrbit.orbit.orbiting));
    } catch {
      setInvalidInput(true);
      setLeftOpen(true);
      return
    }

    // make sure that the flyby sequence contains appropriate bodies.
    for(let i=0; i<flybyIdSequence.length; i++) {
      invalid = transferBody.orbiterIds.includes(flybyIdSequence[i]) ? invalid : true;
    }

    // display a warning and do not search for a trajectory if the inputs are invalid
    setInvalidInput(invalid);
    if(invalid) {
      setLeftOpen(true);
      return
    }

    const newMfSearchInputs = searchInputsFromUI(system, startOrbit.orbit, endOrbit.orbit, flybyIdSequence, earlyStartDate, lateStartDate, flightTimes, DSNperLeg, controlsOptionsState, timeSettings);
    setMfSearchInputs(newMfSearchInputs);
    setButtonPresses(buttonPresses + 1);
    setPlotOpen(true); // Auto-open optimizer plot

    console.log('"Search Trajectories" button pressed.');
  }

  useEffect(() => {
    if(multiFlyby.deltaV > 0) {
      setRightOpen(true);
    }
  }, [multiFlyby.deltaV]);

  useEffect(() => {
    if(multiFlyby.deltaV === 0) {
      setMultiFlyby(blankMultiFlyby(system));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system])

  const companionButtons = (
    <IconButton 
        size="small"
        onClick={() => setPlotOpen(!plotOpen)} 
        style={{ 
            backgroundColor: plotOpen ? 'rgba(0, 243, 255, 0.2)' : 'rgba(0, 243, 255, 0.1)',
            border: '1px solid rgba(0, 243, 255, 0.3)',
            backdropFilter: 'blur(4px)',
            color: '#00f3ff',
            pointerEvents: 'auto',
        }}
    >
        <EqualizerIcon />
    </IconButton>
  );

  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <CssBaseline />
      
      {/* Background Layer: 3D Orbit Display */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <OrbitDisplayTabs />
      </div>

      <ScanningOverlay isCalculating={calculating} />

      {/* UI Overlay Layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}>
        <Navbar showHelp={showHelp} setShowHelp={setShowHelp} />
        
        {/* Floating Header */}
        <div style={{ margin: '16px 32px', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 800, textShadow: '0 0 10px rgba(0, 243, 255, 0.5)' }}>
              MULTI-FLYBY PLANNER
            </Typography>
            <HelpCollapse showHelp={showHelp} />
            <Collapse in={invalidInput}>
              <Alert severity='error' sx={{ mt: 1 }}>
                <AlertTitle>Error</AlertTitle>
                Mission inputs are invalid. Check Mission Parameters.
              </Alert>
            </Collapse>
          </div>
        </div>

        {/* Dashboard Panels */}
        <DashboardPanel 
          side="left" 
          title="Mission Parameters" 
          isOpen={leftOpen} 
          onToggle={() => setLeftOpen(!leftOpen)}
          width={360}
        >
          <MissionControls />
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <LoadingButton 
              variant="contained" 
              fullWidth
              size="large"
              loadingPosition="end"
              endIcon={<SearchIcon />}
              loading={calculating}
              onClick={() => handleButtonPress()}
              sx={{ py: 1.5 }}
            >
              Execute Search
            </LoadingButton>
          </div>
        </DashboardPanel>

        <DashboardPanel
          side="center"
          title="Optimizer Progress"
          isOpen={plotOpen}
          onToggle={() => setPlotOpen(false)}
          width={700}
          height={600}
        >
          <EvolutionPlot 
            inputs={mfSearchInputs as MultiFlybySearchInputs}
            buttonPresses={buttonPresses} 
            setCalculating={setCalculating}
          />
        </DashboardPanel>

        <DashboardPanel
          side="right"
          title="Telemetry Data"
          isOpen={rightOpen && multiFlyby.deltaV > 0}
          onToggle={() => setRightOpen(!rightOpen)}
          width={400}
          companionButtons={companionButtons}
        >
          <MultiFlybyInfo />
        </DashboardPanel>
      </div>
    </div>
  )
}

function FlybyApp() {
    return <>{FlybyAppContent()}</>
}
  
export default React.memo(FlybyApp);
